import confirmDelete from './confirmDelete.js';
import uiRouter from '@uirouter/angularjs';

/**
 * @ngdoc service
 * @name eventix.common.services.simpleCRUD
 * @requires ConfirmDelete, $state
 * @description
 * Many parts of an app consist of simple CRUD actions against a REST API. This factory
 * constructs most of the controller logic for such a page.
 *
 * It holds the models, contains a few booleans that reflect current
 * state (editing, busy, etc), and functions that handle CRUD stuff.
 *
 * Up to you to construct a View with both a list of models and a form.
 *
 * simpleCRUD will attempt to manage an optional `editing` URL parameter to
 * keep track of internal state in between refreshes and maintain a functioning
 * Back-button should you visually hide the list/form depending on state.
 *
 * @param {Array<Object>} models Existing models
 * @param {Function} newModelFn Function that returns a new model
 * @returns {Object} A controller like object containing REST functionality
 */
export default angular.module('eventix.common.services.simpleCRUD',[confirmDelete, uiRouter])
    .factory('simpleCRUD', function(ConfirmDelete, $q, $state, $timeout, sortByPredecessor, $location, $transitions) {
        return function CRUD(models, newModelFn, scope) {
            const vm = {};
            var _relationships = [];
            var _ALL_ = {};

            if(scope && scope.$on) {
                scope.$on('input-blur', (ev, name) => {
                    if(typeof vm.model.validate === 'function')
                        vm.model.validate(name);
                    scope.$applyAsync();
                });
            }
            let deregisterParamWatcher = $transitions.onFinish({ to: $state.current.name }, function(trans) {
                let params = trans.params();
                if(params.new && vm.showing)
                    vm.new();
                if(params.editing && !vm.editing) {
                    let pk = _.get(_.first(vm.models), 'constructor.primaryKey');
                    if(!pk)
                        return;
                    let model = _.find(vm.models, _.set({}, pk, params.editing));
                    if(model)
                        vm.edit(model);
                }
            });
            let deregisterExitWatcher = $transitions.onFinish({ exiting: $state.current.name }, function() {
                deregisterParamWatcher();
                deregisterExitWatcher();
            });

            /**
         * @ngdoc property
         * @type Boolean
         * @name eventix.common.services.simpleCRUD#showing
         * @propertyOf eventix.common.services.simpleCRUD
         * @description
         * True if user is browsing the index
         */
            vm.showing = models.length > 0;
            /**
         * @ngdoc property
         * @kind Boolean
         * @name eventix.common.services.simpleCRUD#editing
         * @propertyOf eventix.common.services.simpleCRUD
         * @description
         * True if user is editing an existing model
         */
            vm.editing = false;
            /**
         * @ngdoc property
         * @kind Boolean
         * @name eventix.common.services.simpleCRUD#busy
         * @propertyOf eventix.common.services.simpleCRUD
         * @description
         * True if HTTP requests are in progress
         */
            vm.busy = false;

            /**
         * @ngdoc property
         * @kind Array<Model>
         * @name eventix.common.services.simpleCRUD#models
         * @propertyOf eventix.common.services.simpleCRUD
         * @description
         * All models being managed by simpleCRUD instance
         */
            vm.models = _.isArray(models) ? models : [];
            /**
         * @ngdoc property
         * @kind Array<Model>
         * @name eventix.common.services.simpleCRUD#model
         * @propertyOf eventix.common.services.simpleCRUD
         * @description
         * The current model being edited by simpleCRUD
         */
            vm.model = newModelFn();

            /**
         * @ngdoc method
         * @name eventix.common.services.simpleCRUD#new
         * @methodOf eventix.common.services.simpleCRUD
         * @description
         * Create new model and show form
         *
         * @returns {simpleCRUD} Returns self
         */
            vm.new = function() {
                vm.showing = false;
                vm.model = newModelFn();
                vm.model.$errors.clear();
                let params = angular.copy($state.params);
                delete params.editing;
                params.new = true;
                $state.go($state.current.name, params);
                return vm;
            };

            /**
         * @ngdoc method
         * @name eventix.common.services.simpleCRUD#save
         * @methodOf eventix.common.services.simpleCRUD
         * @description
         * Create or save model
         *
         * New models are added to the `models` array
         *
         * @param {Boolean} [relations=true] Save belongsToMany relations if creating a model
         * @return {Promise} Resolves with this
         */
            vm.save = function(relations = true) {
                if(vm.model.$invalid)
                    return $q.reject('invalid');
                if(vm.busy)
                    return $q.reject('busy');
                vm.busy = true;
                return vm.model.$save().then(function() {
                    if(!vm.editing && relations) {
                    // save all relationions
                        var promises = _.map(_relationships, relation => {
                            return vm['attach' + relation](_ALL_);
                        });
                        return $q.all(promises);
                    }
                    return $q.resolve(vm);
                }).then(function() {
                    if(!vm.editing)
                        vm.models.push(vm.model);
                    vm.busy = false;
                    vm.editing = false;
                    vm.showing = true;
                    vm.model = newModelFn();
                    let params = angular.copy($state.params);
                    delete params.editing;
                    delete params.new;
                    $state.go($state.current.name, params);
                    return $q.resolve(vm);
                }, function(err) {
                    vm.busy = false;
                    return $q.reject(err);
                });
            };


            /**
         * @ngdoc method
         * @name eventix.common.services.simpleCRUD#edit
         * @methodOf eventix.common.services.simpleCRUD
         * @description
         * Edit an existing model, show form
         *
         * @param {Model} model Element from `vm.models`
         * @returns {simpleCRUD} Return self
         */
            vm.edit = function(model) {
                let pk = model.constructor.primaryKey;
                let params = angular.copy($state.params);
                delete params.new;
                params.editing = model[pk];
                $state.go($state.current.name, params);
                vm.editing = true;
                vm.showing = false;
                vm.model = model;
                return vm;
            };

            /**
         * @ngdoc method
         * @name eventix.common.services.simpleCRUD#del
         * @methodOf eventix.common.services.simpleCRUD
         * @description
         * Open confirmation dialog and delete model
         *
         * @param {Model} model Element from `vm.models`
         * @return {Promise} Resolves if deleted, rejects on `error` or `cancel`
         */
            vm.del = function(model) {
                return ConfirmDelete.open(model.toString()).then(function() {
                    model.deleting = true;
                    return model.$delete().then(function() {
                        var pk = model.constructor.primaryKey;
                        _.remove(vm.models, function(m) {
                            return m[pk] === model[pk];
                        });
                        return $q.resolve(vm);
                    });
                });
            };

            /**
         * @ngdoc method
         * @name eventix.common.services.simpleCRUD#cancel
         * @methodOf eventix.common.services.simpleCRUD
         * @description
         * Reset current form
         * @returns {simpleCRUD} Return self
         */
            vm.cancel = function() {
                if(vm.busy) return vm;
                vm.editing = false;
                vm.showing = true;
                let params = angular.copy($state.params);
                delete params.editing;
                delete params.new;
                $state.go($state.current.name, params);
                _.assign(vm.model, vm.model.$savedState);
                vm.model.$errors.clear();
                vm.model = newModelFn();
                return vm;
            };

            /**
         * @ngdoc method
         * @name eventix.common.services.simpleCRUD#addBelongsToMany
         * @methodOf eventix.common.services.simpleCRUD
         * @description
         * Add function to manage a belongsToMany relationship
         *
         * Adds a `attach[fnSuffix]` and `detach[fnSuffix]` to the
         * CRUD object. Extends the `edit` method to fill the
         * container with children. It also extends the save method
         * to attach children when creating a model.
         *
         * @param {String} childContainer The property name on models that contains child models
         * @param {String} fnSuffix The function suffix of relationship actions; i.e. `model.$attachSUFFIX`
         * @param {Object} callbacks Optional callbacks to run after attach, detach or error
         * ```
         * {
         *   attach: successFn(child),
         *   detach: successFn(child),
         *   query: successFn(model, children),
         *   error: errorFn(error)
         * }
         * ```
         * @returns {simpleCRUD} Returns self
         */
            vm.addBelongsToMany = function(childContainer, fnSuffix, callbacks = {}) {
                _relationships.push(fnSuffix);
                if(!angular.isObject(callbacks))
                    throw new Error('Callbacks object malformatted');
                callbacks = angular.extend({
                    attach: angular.noop,
                    detach: angular.noop,
                    query: angular.noop,
                    error: e => $q.reject(e)
                }, callbacks);

                vm['detach' + fnSuffix] = toggle.bind(vm, 'detach');
                vm['attach' + fnSuffix] = function(model) {
                    if(model === _ALL_)
                        return toggle('attach', vm.model[childContainer] || []);
                    return toggle('attach', model);
                };

                var origEdit = vm.edit;
                vm.edit = function(model) {
                    return model['$query' + fnSuffix]().then(children => {
                        if(_.isFunction(model['$reorder' + fnSuffix]))
                            children = sortByPredecessor(children);
                        Object.defineProperty(model, childContainer, {
                            writable: true,
                            configurable: true,
                            enumerable: false,
                            value: children
                        });

                        $q.resolve(callbacks.query(model, children)).then(() => {
                            return origEdit(model);
                        }, callbacks.error);
                    });
                };
                return vm;

                function toggle(action, models) {
                    if(_.isNil(models))
                        return callbacks.error(new Error('No valid model given'));
                    if(vm.busy && vm.editing)
                        return callbacks.error(new Error('HTTP actions in progress'));
                    if(!_.isArray(models))
                        models = [models];
                    if(!vm.model[childContainer]) {
                        Object.defineProperty(vm.model, childContainer, {
                            value: [],
                            enumerable: false,
                            writable: true,
                            configurable: true
                        });
                    }
                    vm.busy = true;
                    var ppk = vm.model.constructor.primaryKey;

                    let promiseChain = _.reduce(models, (promised, model) => {
                        return promised.then(() => {
                            if(!vm.model[ppk])
                                return $q.resolve( resolve(action, model) );
                            var pk = model.constructor.primaryKey;
                            var find = _.set({}, pk, model[pk]);
                            if(action === 'attach' && vm.editing && _.findIndex(vm.model[childContainer], find) >= 0)
                                return $q.resolve(); // Child is already attached
                            if(action === 'detach' && vm.editing && _.findIndex(vm.model[childContainer], find) < 0)
                                return $q.resolve(); // Child is not attached
                            return vm.model['$' + action + fnSuffix](model)
                                .then( resolve.bind(vm, action, model));
                        });
                    }, $q.resolve());
                    return promiseChain.then(
                        (models) => {
                            vm.busy = false;
                            return models;
                        },
                        error => {
                            vm.busy = false;
                            return callbacks.error(error);
                        });
                }

                function resolve(action, model) {
                    var pk = model.constructor.primaryKey;
                    if(action === 'attach') {
                        if(_.findIndex(vm.model[childContainer], _.set({}, pk, model[pk])) < 0)
                            vm.model[childContainer].push(model);
                    } else {
                        var find = _.set({}, pk, model[pk]);
                        var index = _.findIndex(vm.model[childContainer], find);
                        if(index >= 0)
                            vm.model[childContainer].splice(index, 1);
                    }
                    return callbacks[action](model);
                }
            };

            // Throw the restore in a timeout so it runs on the next digest cycle,
            // thus giving controller the chance to addBelongsToMany or alter the
            // state params.
            $timeout(function() {
                let editing = $state.params.editing;
                let creating = $state.params.new;
                if(editing){
                    let pk = _.get(_.last(models),'constructor.primaryKey');
                    let model = _.find(models, m => m[pk] === editing);
                    if(model)
                        vm.edit(model);
                } else if(creating)
                    vm.showing = false;
            });

            return vm;
        };
    }).name;
